import { watch } from "vue"
import { concat, type PieceFrame, type RecordingBlock, type RecordingPiece, type RecordingSegment, readPieceFrames, RecordingSplitter } from "./recordingPieces"

/**
 * Firefox ignores the keyframe interval that MediaRecorder is asked for and writes a keyframe only every 8–10 seconds,
 * but a video can only start with a keyframe. So a kept part that starts between two keyframes gets its frames up to the
 * next keyframe encoded again, starting with a keyframe of its own. A kept part that ends within a piece gets a copy of
 * that piece without the frames after its end. Both are uploaded as extra blocks, which the cut video uses instead of
 * the original pieces, see assembleRecording.
 *
 * The work for a cut starts in the background once the user stopped moving it, so "Video verwenden" rarely waits for it.
 */

// Browsers don't tell whether MediaRecorder keeps the keyframe interval, and Firefox is the one that ignores it
export const needsReencodedCuts = navigator.userAgent.includes("Firefox/")

// The user is assumed to be happy with the cuts once they did not change for this long
const SETTLE_MILLISECONDS = 3000
// Far above the blocks of the recording's pieces and the cluster headers, see assembleRecording
const FIRST_BLOCK_INDEX = 500_000
// More than MediaRecorder uses, so the encoded frames don't look worse than the rest of the video
const BITRATE = 5_000_000
// Decoded frames take a lot of memory, so only a few wait for the encoder, which is much slower than the decoder
const MAX_QUEUED_FRAMES = 4

type TimedElement = { timestamp: number; element: Uint8Array }
type Head = { start: number; end: number; groupTimestamp: number; sourcePieces: RecordingPiece[] }
type Tail = { piece: RecordingPiece; end: number }
type Plan = { head: Head | null; body: RecordingPiece[]; tail: Tail | null }
type Job = { promise: Promise<RecordingPiece | null>; controller: AbortController; isDone: boolean }
type Source = Promise<{ pieceData: Map<number, Uint8Array>; videoTrackNumber: number }>

/**
 * Re-encodes and uploads the parts of a recording around the cuts that fall between keyframes.
 */
export class CutReencoder {
  private jobs = new Map<string, Job>()
  private nextBlockIndex = FIRST_BLOCK_INDEX
  private settleTimeout: ReturnType<typeof setTimeout> | undefined
  private stopWatching: () => void

  constructor(
    private recording: Blob,
    private pieces: RecordingPiece[],
    private upload: (blocks: RecordingBlock[]) => Promise<void>,
    getSegments: () => RecordingSegment[],
  ) {
    this.stopWatching = watch(getSegments, (segments) => {
      // The work for a cut that no longer exists stops right away
      this.cancelJobsExcept(segments.map((segment) => this.plan(segment)))
      clearTimeout(this.settleTimeout)
      this.settleTimeout = setTimeout(() => this.prepare(getSegments()).catch(() => {}), SETTLE_MILLISECONDS)
    })
  }

  /**
   * Returns the pieces of each segment, once the parts around its cuts are encoded again and uploaded.
   */
  prepare(segments: RecordingSegment[]): Promise<RecordingPiece[][]> {
    const plans = segments.map((segment) => this.plan(segment))
    this.cancelJobsExcept(plans)
    const missingHeads: Head[] = []
    const missingTails: Tail[] = []
    for (const { head, tail } of plans) {
      if (head && !this.jobs.has(headKey(head))) {
        missingHeads.push(head)
      }
      if (tail && !this.jobs.has(tailKey(tail))) {
        missingTails.push(tail)
      }
    }
    if (missingHeads.length || missingTails.length) {
      const source = this.readPieces(new Set([...missingHeads.flatMap((head) => head.sourcePieces), ...missingTails.map((tail) => tail.piece)].map((piece) => piece.blockIndex)))
      missingHeads.forEach((head) => this.startJob(headKey(head), (signal) => this.encodeHead(head, source, signal)))
      missingTails.forEach((tail) => this.startJob(tailKey(tail), (signal) => this.shortenTail(tail, source, signal)))
    }
    return Promise.all(
      plans.map(async (plan) => {
        const [head, tail] = await Promise.all([plan.head && this.jobs.get(headKey(plan.head))!.promise, plan.tail && this.jobs.get(tailKey(plan.tail))!.promise])
        return [head, ...plan.body, tail].filter((piece) => !!piece)
      }),
    )
  }

  stop() {
    this.stopWatching()
    clearTimeout(this.settleTimeout)
    this.cancelJobsExcept([])
  }

  /**
   * Splits a segment into the frames from its start up to the next keyframe, which are encoded again,
   * the pieces that it keeps as they are, and its last piece, which is shortened when the segment ends within it.
   */
  private plan(segment: RecordingSegment): Plan {
    const start = Math.round(segment.start * 1000)
    const end = Math.round(segment.end * 1000)
    const cutPointPieces = this.pieces.filter((piece) => piece.isCutPoint)
    const startsAtKeyframe = cutPointPieces.some((piece) => piece.start === start)
    let bodyStart = start
    let head: Head | null = null
    if (!startsAtKeyframe) {
      bodyStart = Math.min(end, cutPointPieces.find((piece) => piece.start > start)?.start ?? end)
      const keyframeBefore = cutPointPieces.findLast((piece) => piece.start <= start)!.start
      head = {
        start,
        end: bodyStart,
        // The frames are timed like the piece that the segment starts in, see RecordingPiece
        groupTimestamp: this.pieces.findLast((piece) => piece.start <= start)!.groupTimestamp,
        // The frames before the segment's start are needed to decode the ones after it
        sourcePieces: this.pieces.filter((piece) => piece.start >= keyframeBefore && piece.start < bodyStart),
      }
    }
    const body = this.pieces.filter((piece) => piece.start >= bodyStart && piece.start < end)
    const lastPiece = body.at(-1)
    if (lastPiece && end <= lastPiece.end) {
      return { head, body: body.slice(0, -1), tail: { piece: lastPiece, end } }
    }
    return { head, body, tail: null }
  }

  private cancelJobsExcept(plans: Plan[]) {
    const neededKeys = new Set(plans.flatMap((plan) => [plan.head && headKey(plan.head), plan.tail && tailKey(plan.tail)]))
    for (const [key, job] of this.jobs) {
      // Finished jobs are kept, because the cut may come back
      if (!job.isDone && !neededKeys.has(key)) {
        job.controller.abort()
        this.jobs.delete(key)
      }
    }
  }

  private startJob(key: string, work: (signal: AbortSignal) => Promise<RecordingPiece | null>) {
    const controller = new AbortController()
    const job: Job = { promise: work(controller.signal), controller, isDone: false }
    this.jobs.set(key, job)
    job.promise.then(
      () => (job.isDone = true),
      // A failed job is tried again with the next preparation
      () => this.jobs.get(key) === job && this.jobs.delete(key),
    )
  }

  /**
   * The pieces are not kept once they are uploaded, so the recording is split again to get the ones that are needed.
   */
  private async readPieces(blockIndexes: Set<number>): Source {
    const splitter = new RecordingSplitter()
    const pieceData = new Map<number, Uint8Array>()
    const keep = (blocks: RecordingBlock[]) => blocks.filter((block) => blockIndexes.has(block.index)).forEach((block) => pieceData.set(block.index, block.data))
    const reader = this.recording.stream().getReader()
    for (let chunk = await reader.read(); !chunk.done; chunk = await reader.read()) {
      keep(splitter.push(chunk.value))
      if (pieceData.size === blockIndexes.size) {
        await reader.cancel()
        return { pieceData, videoTrackNumber: splitter.videoTrackNumber }
      }
    }
    keep(splitter.finish())
    return { pieceData, videoTrackNumber: splitter.videoTrackNumber }
  }

  private async encodeHead(head: Head, source: Source, signal: AbortSignal) {
    const { pieceData, videoTrackNumber } = await source
    const frames = head.sourcePieces.flatMap((piece) => [...readPieceFrames(piece, pieceData.get(piece.blockIndex)!)]).filter((frame) => frame.timestamp < head.end)
    const videoFrames = await encodeVideo(
      frames.filter((frame) => frame.track === videoTrackNumber),
      head.start,
      signal,
    )
    const audioFrames = frames.filter((frame) => frame.track !== videoTrackNumber && frame.timestamp >= head.start)
    const elements = [
      ...videoFrames.map((frame) => ({ timestamp: frame.timestamp, element: simpleBlock(videoTrackNumber, frame.timestamp - head.groupTimestamp, frame.isKeyframe, frame.data) })),
      ...audioFrames.map((frame) => ({ timestamp: frame.timestamp, element: retimedElement(frame, frame.timestamp - head.groupTimestamp) })),
    ].sort((first, second) => first.timestamp - second.timestamp)
    return this.uploadPiece(elements, { isCutPoint: true, groupTimestamp: head.groupTimestamp }, signal)
  }

  private async shortenTail(tail: Tail, source: Source, signal: AbortSignal) {
    const { pieceData } = await source
    const frames = [...readPieceFrames(tail.piece, pieceData.get(tail.piece.blockIndex)!)].filter((frame) => frame.timestamp < tail.end)
    return this.uploadPiece(frames, tail.piece, signal)
  }

  private async uploadPiece(elements: TimedElement[], timing: Pick<RecordingPiece, "isCutPoint" | "groupTimestamp">, signal: AbortSignal) {
    signal.throwIfAborted()
    if (!elements.length) {
      return null
    }
    const data = concat(elements.map((element) => element.element))
    const piece = { ...timing, blockIndex: this.nextBlockIndex++, size: data.length, start: elements[0].timestamp, end: elements.at(-1)!.timestamp }
    await this.upload([{ index: piece.blockIndex, data }])
    return piece
  }
}

function headKey(head: Head) {
  return `head ${head.start} ${head.end}`
}

function tailKey(tail: Tail) {
  return `tail ${tail.piece.blockIndex} ${tail.end}`
}

/**
 * Decodes the frames, which start with a keyframe, and encodes the ones from the given time on again, starting with a keyframe.
 */
async function encodeVideo(frames: PieceFrame[], start: number, signal: AbortSignal) {
  signal.throwIfAborted()
  const encodedFrames: { timestamp: number; isKeyframe: boolean; data: Uint8Array }[] = []
  // Stops when the cut changed in the meantime or one of the codecs failed
  const stopped = new AbortController()
  const encoder = new VideoEncoder({
    output: (chunk) => {
      const data = new Uint8Array(chunk.byteLength)
      chunk.copyTo(data)
      encodedFrames.push({ timestamp: chunk.timestamp / 1000, isKeyframe: chunk.type === "key", data })
    },
    error: (error) => stopped.abort(error),
  })
  const decoder = new VideoDecoder({
    output: (videoFrame) => {
      if (videoFrame.timestamp >= start * 1000 && encoder.state !== "closed") {
        const isFirstFrame = encoder.state === "unconfigured"
        if (isFirstFrame) {
          encoder.configure({ codec: "vp8", width: videoFrame.displayWidth, height: videoFrame.displayHeight, bitrate: BITRATE, latencyMode: "realtime" })
        }
        encoder.encode(videoFrame, { keyFrame: isFirstFrame })
      }
      videoFrame.close()
    },
    error: (error) => stopped.abort(error),
  })
  const closeCodecs = () => [decoder, encoder].filter((codec) => codec.state !== "closed").forEach((codec) => codec.close())
  signal.addEventListener("abort", () => stopped.abort(signal.reason), { once: true })
  stopped.signal.addEventListener("abort", closeCodecs, { once: true })
  try {
    decoder.configure({ codec: "vp8" })
    for (const frame of frames) {
      stopped.signal.throwIfAborted()
      decoder.decode(new EncodedVideoChunk({ type: frame.isKeyframe ? "key" : "delta", timestamp: frame.timestamp * 1000, data: frame.data }))
      while (!stopped.signal.aborted && decoder.decodeQueueSize + encoder.encodeQueueSize > MAX_QUEUED_FRAMES) {
        await new Promise((resolve) => {
          decoder.addEventListener("dequeue", resolve, { once: true })
          encoder.addEventListener("dequeue", resolve, { once: true })
          stopped.signal.addEventListener("abort", resolve, { once: true })
        })
      }
    }
    await decoder.flush()
    if (encoder.state === "configured") {
      await encoder.flush()
    }
    stopped.signal.throwIfAborted()
  } finally {
    closeCodecs()
  }
  return encodedFrames
}

/**
 * A frame as SimpleBlock element, see readPieceFrames. MediaRecorder numbers the tracks from 1, so a track number fits into one byte.
 */
function simpleBlock(track: number, relativeTimestamp: number, isKeyframe: boolean, data: Uint8Array) {
  const element = new Uint8Array(13 + data.length)
  const view = new DataView(element.buffer)
  view.setUint8(0, 0xa3)
  // An 8 byte size: the length marker in the first byte, followed by the size in 7 bytes
  view.setBigUint64(1, (1n << 56n) | BigInt(4 + data.length))
  view.setUint8(9, 0x80 | track)
  view.setInt16(10, relativeTimestamp)
  view.setUint8(12, isKeyframe ? 0x80 : 0)
  element.set(data, 13)
  return element
}

/**
 * A copy of the frame's element, timed relative to another cluster timestamp.
 */
function retimedElement(frame: PieceFrame, relativeTimestamp: number) {
  const element = frame.element.slice()
  new DataView(element.buffer).setInt16(frame.timestampView.byteOffset - frame.element.byteOffset, relativeTimestamp)
  return element
}
